if (!customElements.get('testimonial-carousel')) {
  class TestimonialCarousel extends HTMLElement {
    constructor() {
      super();

      this.track = null;
      this.slides = [];
      this.previousButton = null;
      this.nextButton = null;
      this.resizeObserver = null;
      this.rafId = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.updateControls = this.updateControls.bind(this);
      this.goPrevious = this.goPrevious.bind(this);
      this.goNext = this.goNext.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-testimonial-track]');
      this.slides = Array.from(this.querySelectorAll('[data-testimonial-slide]'));
      this.previousButton = this.querySelector('[data-testimonial-previous]');
      this.nextButton = this.querySelector('[data-testimonial-next]');

      if (!this.track || this.slides.length === 0) return;

      this.previousButton?.addEventListener('click', this.goPrevious);
      this.nextButton?.addEventListener('click', this.goNext);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateControls);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.updateControls, { passive: true });
      }

      requestAnimationFrame(this.updateControls);
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.goPrevious);
      this.nextButton?.removeEventListener('click', this.goNext);
      this.track?.removeEventListener('scroll', this.handleScroll);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.updateControls);

      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    handleScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.updateControls();
      });
    }

    getColumnsPerPage() {
      const value = getComputedStyle(this).getPropertyValue('--tc-columns-current');
      return Math.max(parseInt(value, 10) || 1, 1);
    }

    getStep() {
      if (this.slides.length < 2) return this.track.clientWidth;

      const itemDistance = Math.abs(this.slides[1].offsetLeft - this.slides[0].offsetLeft);
      return itemDistance * this.getColumnsPerPage();
    }

    scrollByPage(direction) {
      const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const destination = Math.min(
        Math.max(this.track.scrollLeft + this.getStep() * direction, 0),
        maxScroll
      );

      this.track.scrollTo({
        left: destination,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    goPrevious() {
      this.scrollByPage(-1);
    }

    goNext() {
      this.scrollByPage(1);
    }

    scrollToSlide(slide) {
      if (!slide || !this.track) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: 'smooth'
      });
    }

    updateControls() {
      if (!this.track) return;

      const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const hasOverflow = maxScroll > 2;
      const currentScroll = Math.max(this.track.scrollLeft, 0);

      if (this.previousButton) {
        this.previousButton.hidden = !hasOverflow;
        this.previousButton.disabled = currentScroll <= 2;
      }

      if (this.nextButton) {
        this.nextButton.hidden = !hasOverflow;
        this.nextButton.disabled = currentScroll >= maxScroll - 2;
      }
    }
  }

  customElements.define('testimonial-carousel', TestimonialCarousel);
}

if (window.Shopify?.designMode && !window.testimonialCarouselEditorEventsBound) {
  window.testimonialCarouselEditorEventsBound = true;

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-testimonial-slide]');
    const carousel = event.target.closest('testimonial-carousel');

    if (slide && carousel && typeof carousel.scrollToSlide === 'function') {
      carousel.scrollToSlide(slide);
    }
  });
}